/*
 * The model on disk. A warm boot reads it back and renders before the socket is even open; a
 * write is debounced a second and flushed when the tab hides, never blocking a render
 * (spec 12 §2.2). Only the model stores are written — drafts and the outbox are a database of
 * their own (core/drafts/db.ts) and out of reach from here, so a wipe cannot lose typed text (A19).
 *
 * The stored model is a cache, not a record: a different `userId` or `MODEL_VERSION` deletes it
 * and cold-boots rather than migrating (protocol.md §6). `serverBootId` is stored for information
 * only — a server restart is invisible (protocol.md §8 R10). Core's own fields are read back field by
 * field, so a field added to the model since the write is simply absent from the cache and arrives at
 * its empty value; a module's slice is opaque here and goes to disk through the module's own
 * `dehydrate` and comes back through its `hydrate`, which is the same guarantee for a shape core can
 * neither read nor judge (`core/modules/registry.ts`).
 *
 * Writes follow identity, down to the single row. Reducers keep the identity of everything they did
 * not touch, so comparing the model with the last one written says exactly which rows changed: an
 * arriving message is one `put` and, once the window stands at its cap of 200 rows (Q19), one
 * `delete` of the row that fell out — not a rewrite of the window, and not a rewrite of every
 * window. The model read back from disk is what the first write of a visit is compared against, so
 * a warm boot writes what the boot changed and not the 20 000 rows it just read.
 *
 * That comparison holds only as far as this tab is the last one to have written, and it is not the
 * only one: every tab of the account persists the same model into the same database. A window is
 * therefore written against the run of rows that is really on disk — each conversation row carries
 * the bounds of the run its window write left behind — and a run this tab did not leave there is
 * written whole (`writeConversation`), the way it was before the difference existed.
 */

import type { IDBPObjectStore, IDBPTransaction } from "idb"
import type { StoreApi } from "zustand/vanilla"
import {
	MODEL_VERSION,
	emptyModel,
	type ConversationId,
	type ConversationSlice,
	type MessageSlice,
	type MessageView,
	type Model,
} from "../store/model.ts"
import { MODEL_STORES, type Db } from "./idb.ts"

/** The newest rows of one conversation kept on disk (Q19). */
export const MAX_PERSISTED_MESSAGES = 200

export const SAVE_DEBOUNCE_MS = 1000

/** Consecutive failed writes after which the cache is given up, and said to be given up. */
export const SAVE_FAILURES_BEFORE_MEMORY = 2

interface MetaRow {
	k: string
	v: unknown
}

interface StoredMeta {
	userId: number
	modelVersion: number
	lastEventId: number
	serverBootId: string
	savedAt: number
}

/** The rows of one conversation standing in the `messages` store: one contiguous run, by seq. */
interface StoredRun {
	first: number
	last: number
}

/** A conversation row: the slice and, when a window was loaded, its bounds; the rows live apart. */
interface ConversationRow {
	id: ConversationId
	slice: ConversationSlice
	window?: Omit<MessageSlice, "rows">
	/**
	 * The run of rows the write that put this row left in the `messages` store. It is what a later
	 * write compares its own copy against before writing a difference: this database has more than
	 * one writer, and a run another tab left behind is not a difference anybody can write against
	 * (`writeConversation`). Absent when no rows are stored — and in a cache written before this
	 * field existed, which is read as exactly that.
	 */
	run?: StoredRun
}

interface ModuleRow {
	id: string
	slice: unknown
}

const META_KEYS = {
	meta: "meta",
	me: "me",
	settings: "settings",
	notificationPrefs: "notificationPrefs",
	limits: "limits",
	activityUnread: "activityUnread",
	conversationOrder: "conversationOrder",
	enabledModules: "enabledModules",
} as const

/**
 * What this layer needs of a frontend module to bring its slice back: its id and its own `hydrate`,
 * where the rule is written (`core/modules/registry.ts`). `registry.all()` satisfies it; the shape is
 * declared here rather than imported so that the persistence layer depends on nothing but the one
 * question it asks.
 */
export interface SliceOwner {
	id: string
	hydrate?: (stored: unknown) => unknown
	dehydrate?: (slice: unknown) => unknown
}

/**
 * The stored model for this account, or `null` when there is none to warm-boot from — and after
 * clearing a stale one: a stored model of another account or of an older `MODEL_VERSION` is
 * deleted on sight, never read.
 *
 * `owners` are the modules whose slices may be in it. A slice core knows nothing about is restored
 * verbatim; a module that answers `hydrate` is asked what a new visit should see instead, which is how
 * a field added since the write arrives at its empty value and a field belonging to one visit is left
 * behind (registry.ts).
 */
export async function loadModel(
	db: Db,
	userId: number,
	owners: readonly SliceOwner[] = [],
): Promise<Model | null> {
	try {
		const metaRows = (await db.getAll("meta")) as MetaRow[]
		const meta = new Map(metaRows.map((row) => [row.k, row.v]))
		const stored = meta.get(META_KEYS.meta) as StoredMeta | undefined
		if (!stored || stored.modelVersion !== MODEL_VERSION || stored.userId !== userId) {
			if (metaRows.length > 0) await clearModel(db)
			return null
		}
		const model = await readModel(db, meta, stored, owners)
		// A stored cursor with no account behind it is a model a revocation wiped while a write was
		// still on its way: a warm boot from it would catch up over nothing and draw an empty shell.
		if (model.me === null || model.users[userId] === undefined) {
			await clearModel(db)
			return null
		}
		return model
	} catch {
		// A torn or unreadable cache is worth nothing; the cold boot rebuilds it.
		await clearModel(db)
		return null
	}
}

/** One stored module slice as a new visit should see it: its owner's answer, or the value as written. */
function restoredSlice(owners: readonly SliceOwner[], id: string, slice: unknown): unknown {
	const hydrate = owners.find((owner) => owner.id === id)?.hydrate
	return hydrate === undefined ? slice : hydrate(slice)
}

async function readModel(
	db: Db,
	meta: Map<string, unknown>,
	stored: StoredMeta,
	owners: readonly SliceOwner[],
): Promise<Model> {
	const model = emptyModel()
	model.persistence = "idb"
	model.me = (meta.get(META_KEYS.me) as Model["me"] | undefined) ?? null
	model.settings = (meta.get(META_KEYS.settings) as Model["settings"] | undefined) ?? {}
	model.notificationPrefs =
		(meta.get(META_KEYS.notificationPrefs) as Model["notificationPrefs"] | undefined) ?? null
	model.limits = (meta.get(META_KEYS.limits) as Model["limits"] | undefined) ?? null
	model.activityUnread = (meta.get(META_KEYS.activityUnread) as number | undefined) ?? 0
	// Which modules the instance runs, as the last bootstrap said: a warm boot that forgot it would
	// draw, and bind, every module this build has (`core/modules/slots.ts` moduleActive).
	model.enabledModules =
		(meta.get(META_KEYS.enabledModules) as readonly string[] | null | undefined) ?? null
	model.conversationOrder =
		(meta.get(META_KEYS.conversationOrder) as ConversationId[] | undefined) ?? []
	model.meta = {
		lastEventId: stored.lastEventId,
		modelVersion: MODEL_VERSION,
		serverBootId: stored.serverBootId,
	}
	for (const user of (await db.getAll("users")) as Model["users"][number][]) {
		model.users[user.id] = user
	}
	for (const group of (await db.getAll("groups")) as Model["groups"][number][]) {
		model.groups[group.id] = group
	}
	for (const row of (await db.getAll("modules")) as ModuleRow[]) {
		model.modules[row.id] = restoredSlice(owners, row.id, row.slice)
	}
	for (const row of (await db.getAll("conversations")) as ConversationRow[]) {
		if (!row.window) {
			model.conversations[row.id] = { ...row.slice, status: "cold" }
			continue
		}
		const rows = (await db.getAllFromIndex("messages", "by_conv", row.id)) as MessageView[]
		model.conversations[row.id] = { ...row.slice, status: "ready" }
		// The bounds are the *restored* window's, never the written one's: only the newest rows are
		// kept, so a reader who had paged far into the past stored a `firstSeq` hundreds of messages
		// below the oldest row that came back. "Load earlier" asks the server for what stands before
		// `firstSeq`, and the page would then be joined to the window with everything between them
		// missing — no divider, no gap, nothing to see (the gap rule guards arriving events alone).
		model.messages[row.id] = {
			...row.window,
			rows,
			firstSeq: rows[0]?.seq ?? row.window.firstSeq,
			lastSeq: rows.at(-1)?.seq ?? row.window.lastSeq,
		}
	}
	return model
}

/** Writes what changed since `previous` (everything when there is no previous). */
export async function saveModel(
	db: Db,
	model: Model,
	previous: Model | null,
	owners: readonly SliceOwner[] = [],
): Promise<void> {
	if (!model.me) return
	const tx = db.transaction(
		MODEL_STORES.map((s) => s.name),
		"readwrite",
	)
	const meta = tx.objectStore("meta")
	const stored: StoredMeta = {
		userId: model.me.id,
		modelVersion: MODEL_VERSION,
		lastEventId: model.meta.lastEventId,
		serverBootId: model.meta.serverBootId,
		savedAt: Date.now(),
	}
	const writes: Promise<unknown>[] = [meta.put({ k: META_KEYS.meta, v: stored } satisfies MetaRow)]
	const putMeta = (k: string, v: unknown) => writes.push(meta.put({ k, v } satisfies MetaRow))
	if (!previous || previous.me !== model.me) putMeta(META_KEYS.me, model.me)
	if (!previous || previous.settings !== model.settings) putMeta(META_KEYS.settings, model.settings)
	if (!previous || previous.notificationPrefs !== model.notificationPrefs) {
		putMeta(META_KEYS.notificationPrefs, model.notificationPrefs)
	}
	if (!previous || previous.limits !== model.limits) putMeta(META_KEYS.limits, model.limits)
	if (!previous || previous.enabledModules !== model.enabledModules) {
		putMeta(META_KEYS.enabledModules, model.enabledModules)
	}
	if (!previous || previous.activityUnread !== model.activityUnread) {
		putMeta(META_KEYS.activityUnread, model.activityUnread)
	}
	if (!previous || previous.conversationOrder !== model.conversationOrder) {
		putMeta(META_KEYS.conversationOrder, model.conversationOrder)
	}

	if (!previous || previous.users !== model.users) {
		writes.push(rewrite(tx, "users", Object.values(model.users)))
	}
	if (!previous || previous.groups !== model.groups) {
		writes.push(rewrite(tx, "groups", Object.values(model.groups)))
	}
	if (!previous || previous.modules !== model.modules) {
		writes.push(saveModules(tx, model, previous, owners))
	}
	if (
		!previous ||
		previous.conversations !== model.conversations ||
		previous.messages !== model.messages
	) {
		writes.push(saveConversations(tx, model, previous))
	}
	await Promise.all(writes)
	await tx.done
}

type Tx = IDBPTransaction<unknown, string[], "readwrite">

async function rewrite(tx: Tx, name: string, rows: unknown[]): Promise<void> {
	const store = tx.objectStore(name)
	await store.clear()
	await Promise.all(rows.map((row) => store.put(row)))
}

/** What of one module's slice is kept: its owner's answer, or the slice as it stands. */
function storedSlice(owners: readonly SliceOwner[], id: string, slice: unknown): unknown {
	const dehydrate = owners.find((owner) => owner.id === id)?.dehydrate
	return dehydrate === undefined ? slice : dehydrate(slice)
}

/**
 * Whether two projections of a slice are the same write. One level of identity is the whole test,
 * because a projection is a handful of the slice's own fields and a reducer keeps the identity of
 * every field it did not touch: a typing frame replaces `typing` and leaves `self`, `cursors` and
 * `threadSeen` where they were, so the projection compares equal and nothing is written.
 */
function sameSlice(one: unknown, other: unknown): boolean {
	if (Object.is(one, other)) return true
	if (typeof one !== "object" || one === null || typeof other !== "object" || other === null) {
		return false
	}
	const left = one as Record<string, unknown>
	const right = other as Record<string, unknown>
	const keys = Object.keys(left)
	if (keys.length !== Object.keys(right).length) return false
	return keys.every((key) => Object.is(left[key], right[key]))
}

/**
 * One row per module, written by identity like everything else here: a module whose stored part did
 * not change is not written, and a module that has left the build has its row deleted. There is no
 * `clear()` over the store — the rows are keyed by module id, so clearing was only ever about
 * dropping a module that has gone, and it made every other module's row a rewrite whenever any one
 * of them moved.
 *
 * With no previous model there is nothing to compare against and nothing known about what is on
 * disk, so the store is emptied and written whole — the cold boot, which happens once per visit.
 */
async function saveModules(
	tx: Tx,
	model: Model,
	previous: Model | null,
	owners: readonly SliceOwner[],
): Promise<void> {
	const store = tx.objectStore("modules")
	if (!previous) {
		await store.clear()
		await Promise.all(
			Object.entries(model.modules).map(([id, slice]) =>
				store.put({ id, slice: storedSlice(owners, id, slice) } satisfies ModuleRow),
			),
		)
		return
	}
	const writes: Promise<unknown>[] = []
	for (const id of Object.keys(previous.modules)) {
		if (!(id in model.modules)) writes.push(store.delete(id))
	}
	for (const [id, slice] of Object.entries(model.modules)) {
		const kept = storedSlice(owners, id, slice)
		if (id in previous.modules && sameSlice(kept, storedSlice(owners, id, previous.modules[id]))) {
			continue
		}
		writes.push(store.put({ id, slice: kept } satisfies ModuleRow))
	}
	await Promise.all(writes)
}

type MessageStore = IDBPObjectStore<unknown, string[], "messages", "readwrite">

type ConversationStore = IDBPObjectStore<unknown, string[], "conversations", "readwrite">

/** The rows of one window as they are kept: the newest `MAX_PERSISTED_MESSAGES` of it (Q19). */
function storedRows(window: MessageSlice | undefined): readonly MessageView[] {
	if (!window) return []
	if (window.rows.length <= MAX_PERSISTED_MESSAGES) return window.rows
	return window.rows.slice(-MAX_PERSISTED_MESSAGES)
}

/** Every stored row of one conversation: the store is keyed `[conv, seq]`, so this is one range. */
function wholeWindow(conv: ConversationId): IDBKeyRange {
	return IDBKeyRange.bound([conv], [conv + 1], false, true)
}

/**
 * One window's rows, and the run they leave behind. What is written is the difference against
 * `previous` — the rows the caller has established are the ones standing on disk — and the reducer
 * keeps the identity of every row it did not touch, so an arriving message is *one* `put` where a
 * rewrite of the window cost one per row. `previous === null` writes every row: nothing on disk is
 * known to compare against.
 *
 * What is *removed* is not diffed but stated: everything outside the run that is kept goes, in two
 * range deletes over the `[conv, seq]` key — below the run and above it. Both are needed however
 * little the window moved, because another tab's run may be standing here; together with a `null`
 * `previous` in that case they replace it, so what is left is one contiguous run of the timeline,
 * which is what `readModel` hands back as a window.
 *
 * The deletes are awaited before the puts: requests run in the order they are made, and a delete
 * queued after a put of the same key would take the fresh row with it.
 */
async function writeWindow(
	messages: MessageStore,
	conv: ConversationId,
	rows: readonly MessageView[],
	previous: readonly MessageView[] | null,
): Promise<StoredRun | undefined> {
	const first = rows[0]?.seq
	const last = rows.at(-1)?.seq
	if (first === undefined || last === undefined) {
		await messages.delete(wholeWindow(conv))
		return undefined
	}
	await Promise.all([
		messages.delete(IDBKeyRange.bound([conv], [conv, first], false, true)),
		messages.delete(IDBKeyRange.bound([conv, last], [conv + 1], true, true)),
	])
	const stored = new Map(previous?.map((row) => [row.seq, row]))
	const changed = previous === null ? rows : rows.filter((row) => stored.get(row.seq) !== row)
	await Promise.all(changed.map((row) => messages.put(row)))
	return { first, last }
}

/**
 * Whether the rows this tab last wrote are the ones standing on disk, which is the whole condition
 * for writing a difference rather than the window. Every tab of the account writes this database,
 * and a tab standing elsewhere in the same conversation replaces the run entirely: diffed against
 * this tab's own last write instead, each write would delete the other tab's rows and put back only
 * the one or two its own model had changed, and the stored window would drain to nothing while the
 * conversation row still claimed a full one.
 *
 * The bounds are the whole test, so two tabs standing at the *same* bounds are not told apart. What
 * that costs is bounded and self-healing: the rows are the same messages, and a row one tab has
 * edited and the other has not is put by whichever tab changed it, on its next write.
 */
function stillOnDisk(run: StoredRun | undefined, written: readonly MessageView[] | null): boolean {
	if (!run || !written) return false
	return run.first === written[0]?.seq && run.last === written.at(-1)?.seq
}

/**
 * One conversation: its slice, the description of its window and the window's rows. The three are
 * written together or not at all, because they only mean anything together — a window's bounds
 * describe the rows of the same write, and the run says whose rows those are.
 *
 * A change that leaves the window alone therefore leaves the stored window alone as well, down to
 * the description: the rows standing there may be another tab's, and this tab's `hasMoreAfter` said
 * of another tab's rows is a lie a warm boot would believe.
 */
async function writeConversation(
	stores: { conversations: ConversationStore; messages: MessageStore },
	entry: { id: ConversationId; slice: ConversationSlice; window: MessageSlice | undefined },
	change: { windowChanged: boolean; written: readonly MessageView[] | null },
): Promise<void> {
	const { id, slice, window } = entry
	// Nothing was written before this save, so there is nothing of this tab's to keep or compare.
	const onDisk =
		change.written === null
			? undefined
			: ((await stores.conversations.get(id)) as ConversationRow | undefined)
	if (!change.windowChanged) {
		const kept: ConversationRow = { id, slice }
		if (onDisk?.window) kept.window = onDisk.window
		if (onDisk?.run) kept.run = onDisk.run
		await stores.conversations.put(kept)
		return
	}
	const previous = stillOnDisk(onDisk?.run, change.written) ? change.written : null
	const run = await writeWindow(stores.messages, id, storedRows(window), previous)
	const row: ConversationRow = { id, slice }
	if (window) {
		const { rows, ...bounds } = window
		row.window = rows.length > MAX_PERSISTED_MESSAGES ? { ...bounds, hasMoreBefore: true } : bounds
	}
	if (run) row.run = run
	await stores.conversations.put(row)
}

/**
 * Conversation rows follow the slice's identity, message windows the window's; a conversation
 * that vanished from the model takes its rows with it. A window in flight is stored as it was
 * last known: `loading`, `resyncPending` and `error` are moments, not state worth a reload.
 */
async function saveConversations(tx: Tx, model: Model, previous: Model | null): Promise<void> {
	const stores = {
		conversations: tx.objectStore("conversations"),
		messages: tx.objectStore("messages"),
	}
	const writes: Promise<unknown>[] = []
	const known = new Set(Object.keys(model.conversations).map(Number))

	for (const key of previous ? Object.keys(previous.conversations) : []) {
		const id = Number(key)
		if (known.has(id)) continue
		writes.push(stores.conversations.delete(id), stores.messages.delete(wholeWindow(id)))
	}
	for (const [key, slice] of Object.entries(model.conversations)) {
		const id = Number(key)
		const window = model.messages[id]
		const sliceChanged = !previous || previous.conversations[id] !== slice
		// A window that goes while its conversation stays was *given back* rather than deleted: a tab
		// buys memory back by dropping the windows nobody is looking at, and the conversation then
		// stands at `cold` with no rows in the model (`modules/chat/state/trim.ts`). The stored copy is
		// not what it holds in memory but what the next boot renders from before the socket is even
		// open, so the disk goes on saying what it said; a conversation's rows are deleted when the
		// conversation itself leaves the model, which is the loop above.
		const windowChanged = !previous || (window !== undefined && previous.messages[id] !== window)
		if (!sliceChanged && !windowChanged) continue
		writes.push(
			writeConversation(
				stores,
				{ id, slice, window },
				{ windowChanged, written: previous ? storedRows(previous.messages[id]) : null },
			),
		)
	}
	await Promise.all(writes)
}

/** Empties the model stores and nothing else: the picked-file bytes stay, the drafts are elsewhere. */
export async function clearModel(db: Db): Promise<void> {
	try {
		const tx = db.transaction(
			MODEL_STORES.map((s) => s.name),
			"readwrite",
		)
		await Promise.all(MODEL_STORES.map((s) => tx.objectStore(s.name).clear()))
		await tx.done
	} catch {
		// A store that cannot be cleared cannot be read either; the next load starts cold.
	}
}

export interface PersistenceOptions {
	/**
	 * The model that was read back from this database, so the first write is the difference against
	 * what is on disk. Without it the first flush after a warm boot rewrites every row the boot just
	 * read — one second after the boot, while the socket is catching up.
	 */
	saved?: Model | null
	debounceMs?: number
	/**
	 * The modules whose slices are in the model, so each may say what of its own is written
	 * (`FrontendModule.dehydrate`). Without them a slice is written whole, which is what every slice
	 * was before a module had a say.
	 */
	owners?: readonly SliceOwner[]
}

/**
 * Keeps the database behind the store: every change is written a second after the last one,
 * and at once when the tab hides. Returns the stop function; the pending write is flushed first,
 * and the promise it returns settles once that write has landed, so a caller that clears the
 * database after it knows nothing is written behind its back.
 *
 * A write that fails is retried on the next change, because the quota is shared with the whole
 * origin and a neighbour's spike passes. Two in a row are not a spike: the cache is then given up
 * for this visit and `persistence` says `memory`, rather than letting every later change repeat a
 * write that cannot land and letting the reader believe in a cache that has silently stopped
 * advancing — a stored cursor that falls behind the change log cold-boots the next visit.
 */
export function startPersistence(
	db: Db,
	api: StoreApi<Model>,
	options: PersistenceOptions = {},
): () => Promise<void> {
	const debounceMs = options.debounceMs ?? SAVE_DEBOUNCE_MS
	const owners = options.owners ?? []
	let lastSaved: Model | null = options.saved ?? null
	let timer: ReturnType<typeof setTimeout> | undefined
	let writing: Promise<void> = Promise.resolve()
	let failures = 0
	let givenUp = false

	const giveUp = () => {
		givenUp = true
		const model = api.getState()
		if (model.persistence !== "memory") api.setState({ ...model, persistence: "memory" }, true)
	}
	const flush = () => {
		if (timer !== undefined) clearTimeout(timer)
		timer = undefined
		if (givenUp) return
		const model = api.getState()
		if (model === lastSaved) return
		const previous = lastSaved
		lastSaved = model
		writing = writing.then(() =>
			saveModel(db, model, previous, owners).then(
				() => {
					failures = 0
				},
				() => {
					// Nothing of this transaction landed, so what is on disk is still `previous`, and
					// the next change writes its difference against that.
					lastSaved = previous
					failures++
					if (failures >= SAVE_FAILURES_BEFORE_MEMORY) giveUp()
				},
			),
		)
	}
	const schedule = () => {
		if (givenUp || timer !== undefined) return
		timer = setTimeout(flush, debounceMs)
	}
	const onVisibility = () => {
		if (document.visibilityState === "hidden") flush()
	}

	const unsubscribe = api.subscribe(schedule)
	document.addEventListener("visibilitychange", onVisibility)
	window.addEventListener("pagehide", flush)
	return () => {
		unsubscribe()
		document.removeEventListener("visibilitychange", onVisibility)
		window.removeEventListener("pagehide", flush)
		flush()
		return writing
	}
}
